import { Info } from '@mui/icons-material';
import {
    FormControlLabel,
    Switch,
    Tooltip,
    type TooltipProps,
} from '@mui/material';
import { useIntl } from 'react-intl';

interface CompactToggleProps {
    labelId: string;
    tooltipId: string;
    /** Interpolation values shared by the label and tooltip messages (e.g.
     *  a structure name), for rows whose text names a live object. */
    messageValues?: Record<string, string | number>;
    value: boolean;
    onChange: (val: boolean) => void;
    /** Disables the switch and its label; the info icon stays hoverable
     *  so the reason (carried by the surrounding UI) stays discoverable. */
    disabled?: boolean;
    /** Test id on the label, which toggles the switch when clicked. */
    dataTestId?: string;
    /**
     * Let the label wrap onto more lines when the row is narrow, instead
     * of holding one line and pushing what follows past the edge. For a
     * panel column; a tool tray keeps its one line.
     */
    wrapLabel?: boolean;
    /**
     * Which way the explanation opens. Unless given it follows the theme:
     * below, or beside the tool options in their dock. Never toward the
     * switch, which is the control it explains.
     */
    tooltipPlacement?: TooltipProps['placement'];
    /** Spread the row across its container, the label first and the switch at the end. */
    fullWidth?: boolean;
}

/**
 * A switch with its label after it, as MUI lays one out (`FormControlLabel`
 * with a `Switch` control), and an Info icon after the label that carries
 * the explanation rather than wrapping the row in a Tooltip. Its look comes
 * from the theme. A full-width row puts the label first and the switch at
 * its far end.
 *
 * A row-wide tooltip would open over the very switch the user is reaching
 * for, hiding what the next click is about to change. The icon is the
 * app-wide idiom (see `Inputs/InlineToggle`) and leaves the control
 * visible while it is read.
 */
export default function CompactToggle({
    labelId,
    tooltipId,
    messageValues,
    value,
    onChange,
    disabled = false,
    dataTestId,
    wrapLabel = false,
    tooltipPlacement,
    fullWidth = false,
}: CompactToggleProps) {
    const intl = useIntl();
    const label = intl.formatMessage({ id: labelId }, messageValues);
    // Resolved to a string rather than left as a <FormattedMessage> node
    // because it is handed to a Tooltip title, not rendered as a child.
    const title = intl.formatMessage({ id: tooltipId }, messageValues);
    return (
        <FormControlLabel
            data-testid={dataTestId}
            disabled={disabled}
            labelPlacement={fullWidth ? 'start' : 'end'}
            control={
                <Switch
                    checked={value}
                    // Named by the label alone, not by the explanation the
                    // label also holds.
                    inputProps={{ 'aria-label': label }}
                    onChange={(_, checked) => onChange(checked)}
                />
            }
            label={
                <>
                    {label}
                    <Tooltip
                        title={title}
                        placement={tooltipPlacement}
                        // A message's blank lines set its paragraphs apart.
                        slotProps={{
                            tooltip: { sx: { whiteSpace: 'pre-line' } },
                        }}
                    >
                        {/* Reading the explanation must not also flip the
                            setting: the icon sits inside the label, whose
                            click toggles the switch, so a click that only
                            meant to dismiss the tooltip would otherwise
                            change a clinical parameter. */}
                        <Info
                            tabIndex={0}
                            role="button"
                            aria-label={title}
                            onClick={(e) => e.preventDefault()}
                            sx={{ fontSize: 12, opacity: 0.6, flexShrink: 0 }}
                        />
                    </Tooltip>
                </>
            }
            slotProps={{
                typography: {
                    noWrap: !wrapLabel,
                    sx: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        ...(wrapLabel && { minWidth: 0 }),
                    },
                },
            }}
            sx={{
                ...(wrapLabel && { minWidth: 0, flex: 1 }),
                ...(fullWidth && {
                    width: '100%',
                    justifyContent: 'space-between',
                }),
            }}
        />
    );
}
